import {useCallback, useContext} from 'react';
import {ApiError, RequestData} from '../types/requests';
import {AuthContext} from "../../features/auth/context/AuthContext";

export const useApiRequest = () => {

    const { token, firebaseUser } = useContext(AuthContext)!;

    // The primary function handling all request logic
    const sendRequest = useCallback(async <T, U = unknown>({
                                                               url,
                                                               method,
                                                               objectToSend,
                                                               fileToSend,
                                                               fileDescriptor = "",
                                                               transform
                                                           }: RequestData<T, U>): Promise<T> => {

        // Always fetch a fresh token right before the request. Firebase caches the
        // token internally and only performs a network refresh if it has expired,
        // so this is cheap and prevents sending stale tokens after tab inactivity
        // (e.g. when the SDK's proactive background refresh timer gets throttled).
        const currentToken = firebaseUser ? await firebaseUser.getIdToken() : token;

        if (!currentToken) {
            throw new Error('No authentication token found');
        }

        const message: RequestInit = {
            method,
            headers: { Authorization: `Bearer ${currentToken}` }
        };

        if (!fileToSend) {
            (message.headers as Record<string, string>)["Content-Type"] = "application/json";
        }

        if (fileToSend) {
            const formData = new FormData();
            formData.append(fileDescriptor, fileToSend);
            if (objectToSend) formData.append("data", JSON.stringify(objectToSend));
            message.body = formData;
        } else if (objectToSend) {
            message.body = JSON.stringify(objectToSend);
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_ENTRYPOINT}${url}`, message);

        if (!response.ok) {
            const data = await response.json();
            throw new ApiError(
                data?.error || 'Request failed',
                data?.code,
                response.status
            );
        }

        if (response.status === 204) {
            return undefined as unknown as T;
        }

        const data = await response.json();
        return transform ? transform(data) : (data as T);

    }, [token, firebaseUser]);

    const getRequest = useCallback(<T,>(url: string, transform?: (data: any) => T) => {
        return sendRequest<T, undefined>({ url, method: "GET", transform });
    }, [sendRequest]);

    const postRequest = useCallback(<T, U = unknown>(url: string, objectToSend?: U, transform?: (data: any) => T) => {
        return sendRequest<T, U>({ url, method: "POST", objectToSend, transform });
    }, [sendRequest]);

    const putRequest = useCallback(<T, U = unknown>(url: string, objectToSend?: U, transform?: (data: any) => T) => {
        return sendRequest<T, U>({ url, method: "PUT", objectToSend, transform });
    }, [sendRequest]);

    const patchRequest = useCallback(<T, U = unknown>(url: string, objectToSend?: U, transform?: (data: any) => T) => {
        return sendRequest<T, U>({ url, method: "PATCH", objectToSend, transform });
    }, [sendRequest]);

    const deleteRequest = useCallback(<T,>(url: string, transform?: (data: any) => T) => {
        return sendRequest<T, undefined>({ url, method: "DELETE", transform });
    }, [sendRequest]);

    return { sendRequest, getRequest, postRequest, putRequest, patchRequest, deleteRequest };
};
